import Box from '@mui/material/Box'
import { PickOrderDetailFragment } from '../../../../graphql'
import { PICK_ORDER_PREFIX } from '../constants'
import { ShippingAddress, cityLine } from '../address'
import { packingSlips } from './packingSlips'

function AddressLines( { address }: { address: ShippingAddress } ) {
	return (
		<>
			{[ address.name, address.companyName, address.line1, address.line2, cityLine( address ) ]
				.filter( Boolean )
				.map( ( line ) => <div key={line}>{line}</div> )}
		</>
	)
}

/**
 * One page per carton, for the box.
 *
 * The transfer order number leads, because it is what the receiving branch
 * looks up to receive the carton into its inventory.
 */
export default function PackingSlipsDocument( { pickOrder }: { pickOrder: PickOrderDetailFragment } ) {
	const slips = packingSlips( pickOrder.fulfillments )

	return (
		<>
			{slips.map( ( slip, index ) => (
				<Box
					key={slip.fulfillmentId}
					sx={{ breakAfter: index === slips.length - 1 ? 'auto' : 'page' }}
				>
					<Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px' }}>
						<Box>
							<Box component="h1" sx={{ fontSize: '20pt', m: 0 }}>Packing slip</Box>
							<Box sx={{ fontSize: '16pt', fontWeight: 700, mt: '4px' }}>
								Carton {index + 1} of {slips.length}
							</Box>
						</Box>
						<Box sx={{ textAlign: 'right' }}>
							<Box sx={{ fontSize: '9pt', textTransform: 'uppercase' }}>Transfer order</Box>
							<Box className="mono" sx={{ fontSize: '24pt', fontWeight: 700 }}>
								{slip.transferOrderId ? `TO-${slip.transferOrderId}` : '—'}
							</Box>
						</Box>
					</Box>

					<Box sx={{ display: 'flex', gap: '24px', my: '12px', lineHeight: 1.5 }}>
						<Box sx={{ flex: 1 }}>
							<div><strong>Pick order:</strong> {PICK_ORDER_PREFIX}{pickOrder.pickOrderNumber}</div>
							<div><strong>Fulfillment orders:</strong> {slip.orderNumbers.map( ( number ) => `FO-${number}` ).join( ', ' )}</div>
							{slip.requestNumbers.length > 0 && (
								<div><strong>Requests:</strong> {slip.requestNumbers.map( ( number ) => `REQ-${number}` ).join( ', ' )}</div>
							)}
							<div><strong>From:</strong> {pickOrder.fulfillingInventory.name}</div>
							{slip.trackingNumber && <div><strong>Tracking:</strong> {slip.trackingNumber}</div>}
						</Box>
						<Box sx={{ flex: 1 }}>
							<strong>Ship to:</strong>
							{pickOrder.shippingAddress
								? <AddressLines address={pickOrder.shippingAddress} />
								: <div>{pickOrder.destinationDescription ?? '—'}</div>}
						</Box>
					</Box>

					<table>
						<thead>
							<tr>
								<th>Part number</th>
								<th>Manufacturer</th>
								<th>Description</th>
								<th>Order</th>
								<th>Request</th>
								<th className="num">Qty</th>
							</tr>
						</thead>
						<tbody>
							{slip.lines.map( ( line ) => (
								<tr key={line.key} style={{ breakInside: 'avoid' }}>
									<td className="mono">{line.identity.sku ?? '—'}</td>
									<td>{line.identity.madeBy ?? '—'}</td>
									<td>{line.identity.description}</td>
									<td>FO-{line.orderNumber}</td>
									<td>{line.requestNumber !== null ? `REQ-${line.requestNumber}` : '—'}</td>
									<td className="num">{line.quantity}</td>
								</tr>
							) )}
							<tr>
								<td colSpan={5} style={{ textAlign: 'right' }}><strong>Total units</strong></td>
								<td className="num"><strong>{slip.units}</strong></td>
							</tr>
						</tbody>
					</table>

					<Box sx={{ mt: '16px', fontSize: '10pt' }}>
						Receive against {slip.transferOrderId ? `TO-${slip.transferOrderId}` : 'its transfer order'}.
					</Box>
				</Box>
			) )}
		</>
	)
}
